import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChartBox } from "@unified-checkout/design-system";
import { LineChart } from "@unified-checkout/design-system";
import { SkeletonLoader } from "@unified-checkout/design-system";
import type { ApexOptions } from "apexcharts";
import type { IHomeApisParams } from "@app/dashboard/types/innerLayout/home";
import type { ILegendItem } from "libs/design-system/src/types/charts/chart_box";
import { getLineChartOptions, labelsDatesFormatter } from "@app/dashboard/utils/charts";
import { useFiltersStore } from "@app/dashboard/store/home/filters";
import { useLangStore } from "@app/dashboard/store/lang";
import { ComponentError } from "@app/dashboard/components/shared/ComponentError";

const Line = (
  {
    queryKey,
    queryFn,
    apiParams,
    timeFormat,
    rangeType
  }
    :
    {
      queryKey: string,
      queryFn: Function,
      apiParams: IHomeApisParams,
      timeFormat: string,
      rangeType: string
    }
) => {

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { filters } = useFiltersStore();
  const { lang } = useLangStore();

  const [chartOptions, setChartOptions] = useState<ApexOptions>();

  const legendList: ILegendItem[] = [
    { color: '#144DFF', label: t('innerLayout.homePage.charts.current') },
    { color: '#D0DBFF', label: t('innerLayout.homePage.charts.previous') },
  ];

  const {
    isLoading,
    isError,
    data: response,
  } = useQuery({
    queryKey: [queryKey, filters],
    queryFn: () => queryFn(apiParams),
    enabled: Object.values(filters).every(value => value !== undefined),
  });

  const chartData = response?.data;
  const mainData = chartData?.data;
  const compareData = chartData?.compare?.data;

  const mainSeries = mainData?.map((obj: any) => obj.value ?? 0) || [];
  const compareSeries = compareData?.map((obj: any) => obj.value ?? 0) || [];

  const titles = mainData?.map((obj: any) => labelsDatesFormatter(obj.title, timeFormat, mainSeries.length, lang) ?? "");

  const { en_currency, ar_currency } = chartData || {};
  const currency = { en: en_currency, ar: ar_currency };

  useEffect(() => {
    return () => queryClient.removeQueries({ queryKey: [queryKey] });
  }, []);

  useEffect(() => {
    setChartOptions(
      getLineChartOptions(
        lang,
        rangeType,
        timeFormat,
        ((mainSeries?.length - 1) || 0),
        currency,
        titles,
      )
    );
  }, [chartData, lang]);

  return (

    (
      isError ?
        <ChartBox>
          <ComponentError text={t('innerLayout.errors.unableToRetrieveData')} />
        </ChartBox>
        :
        isLoading ?
          <SkeletonLoader noOfRows={7} />
          :
          (
            response ?
              <ChartBox
                title={chartData?.[`${lang}_title`]}
                info={chartData?.[`info_${lang}`]}
                legendList={legendList}
                legendPlacement="top"
              >
                <LineChart
                  series={[
                    { data: mainSeries },
                    { data: compareSeries.slice(0, mainSeries?.length) }
                  ]}
                  options={chartOptions}
                />
              </ChartBox>
              :
              <SkeletonLoader noOfRows={7} />
          )
    )
  )
}

export default Line;